import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { habitsApi } from '../api/client';
import type { HabitDefinition } from '../api/client';

interface LogHabitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHabitLogged: () => void;
}

const LogHabitModal: React.FC<LogHabitModalProps> = ({ isOpen, onClose, onHabitLogged }) => {
  const [habits, setHabits] = useState<HabitDefinition[]>([]);
  const [formData, setFormData] = useState({
    habitId: '',
    date: new Date().toISOString().split('T')[0],
    completed: true,
    value: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingHabits, setLoadingHabits] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchHabits();
    }
  }, [isOpen]);

  const fetchHabits = async () => {
    try {
      setLoadingHabits(true);
      const response = await habitsApi.getHabitDefinitions();
      setHabits(response.data.filter(habit => habit.isActive));
    } catch (error) {
      console.error('Error fetching habits:', error);
      setError('Failed to load habits');
    } finally {
      setLoadingHabits(false);
    }
  };

  const resetForm = () => {
    setFormData({
      habitId: '',
      date: new Date().toISOString().split('T')[0],
      completed: true,
      value: '',
      notes: ''
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.habitId) {
      setError('Please select a habit to log');
      return;
    }

    const selectedHabit = habits.find(h => h.id === formData.habitId);
    if (selectedHabit?.targetValue && !formData.value) {
      setError('This habit requires a value');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await habitsApi.createHabitEntry({
        habitId: formData.habitId,
        date: formData.date,
        completed: formData.completed,
        value: formData.value ? parseFloat(formData.value) : undefined,
        unit: selectedHabit?.unit,
        notes: formData.notes || undefined
      });

      setSuccess(true);
      setTimeout(() => {
        onHabitLogged();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error logging habit:', err);
      setError('Failed to log habit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedHabit = habits.find(h => h.id === formData.habitId);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Log Habit Entry">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Habit logged successfully!
          </div>
        )}

        {loadingHabits ? (
          <div className="modal-loading">Loading habits...</div>
        ) : habits.length === 0 ? (
          <div className="modal-info">
            No active habits found. Create a habit first to start logging entries.
          </div>
        ) : (
          <>
            <div className="modal-form-group">
              <label>Select Habit *</label>
              <select
                value={formData.habitId}
                onChange={(e) => setFormData({ ...formData, habitId: e.target.value, value: '' })}
                disabled={loading}
              >
                <option value="">Choose a habit...</option>
                {habits.map((habit) => (
                  <option key={habit.id} value={habit.id}>
                    {habit.name}
                    {habit.targetValue && ` (Target: ${habit.targetValue} ${habit.unit || 'units'})`}
                  </option>
                ))}
              </select>
            </div>

            {selectedHabit && (
              <div className="modal-info" style={{ margin: '0.5rem 0', padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
                <strong>{selectedHabit.name}</strong>
                {selectedHabit.description && <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>{selectedHabit.description}</p>}
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#666' }}>
                  Category: {selectedHabit.category} • Frequency: {selectedHabit.frequency}
                </p>
              </div>
            )}

            <div className="modal-form-row">
              <div className="modal-form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="modal-form-group">
                <label>Status</label>
                <select
                  value={formData.completed ? 'completed' : 'skipped'}
                  onChange={(e) => setFormData({ ...formData, completed: e.target.value === 'completed' })}
                  disabled={loading}
                >
                  <option value="completed">Completed</option>
                  <option value="skipped">Skipped</option>
                </select>
              </div>
            </div>

            {selectedHabit?.targetValue && (
              <div className="modal-form-group">
                <label>
                  Value{selectedHabit.unit && ` (${selectedHabit.unit})`}
                  {selectedHabit.targetValue && ` • Target: ${selectedHabit.targetValue}`}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder={`Enter ${selectedHabit.unit || 'value'}`}
                  disabled={loading}
                />
              </div>
            )}

            <div className="modal-form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes about this entry..."
                disabled={loading}
                rows={2}
              />
            </div>
          </>
        )}

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          {habits.length > 0 && (
            <button
              type="submit"
              className="modal-btn modal-btn-primary"
              disabled={loading || !formData.habitId}
            >
              {loading ? 'Logging...' : 'Log Entry'}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default LogHabitModal;